import { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import { formatMoney, formatDate } from '../../utils/format';
import StatusFilter from '../shared/StatusFilter';
import Tag from '../shared/Tag';
import Modal from '../shared/Modal';
import { useToast } from '../ui/Toast';
import useRefreshOnFocus from '../../hooks/useRefreshOnFocus';
import { toCsv } from '../../utils/csv.js';
import { typeRequiresParent } from '../../utils/sowParent.js';
import { contactOptions, contactOptionText } from '../../utils/clientContacts.js';
import NonCommercialContactSelect from './NonCommercialContactSelect';
import { canAccess } from '../../utils/rbac';
import { surveySummary } from '../../utils/siteSurveys';

const PAGE_SIZE = 25;

// Set by ServiceExpiryPage when a "Renewing" decision is confirmed — this page
// picks it up on mount to auto-open the New Opportunity modal pre-filled with
// the original SOW's client/contact/owner, so a PM just reviews and completes it.
const SERVICE_RENEWAL_PREFILL_KEY = 'av_new_sow_prefill';

const SORT_DIRS = { asc: 'desc', desc: 'asc' };

// Once a SOW has been issued (or progressed beyond), it has been submitted to
// the client — so the submission deadline has been met and is shown in green
// rather than the red/overdue or neutral styling used while it is still in flight.
const SUBMITTED_STATUSES = new Set(['Issued', 'Active', 'Awaiting Payment', 'Complete']);

// Add N days to a YYYY-MM-DD string, returning YYYY-MM-DD (or '' if no date).
const addDaysISO = (d, n) => { if (!d) return ''; const dt = new Date(`${d}T00:00:00`); if (isNaN(dt)) return ''; dt.setDate(dt.getDate() + n); return dt.toISOString().slice(0, 10); };
// Effective expiry for display: the stored expiry_date, or — when a SOW has none
// yet — 56 days after its issued date, so issued SOWs always show a due date.
const effExpiry = (s) => s.expiry_date || addDaysISO(s.issued_date, 56);

const ALL_COLUMNS = [
  { key: 'reference',              label: 'Ref',              right: false, default: true },
  { key: 'status',                 label: 'Status',           right: false, default: true },
  { key: 'owner_name',             label: 'Project Manager',  right: false, default: true },
  { key: 'delivery_owner_name',    label: 'Delivery Owner',   right: false, default: true },
  { key: 'client_name',            label: 'Client',           right: false, default: true },
  { key: 'client_contact_name',    label: 'Client Contact',   right: false, default: true },
  { key: 'detail',               label: 'Detail',           right: false, default: true },
  { key: 'type',                 label: 'Type',             right: false, default: true },
  { key: 'commercial_type',      label: 'Commercial',       right: false, default: true },
  { key: 'submission_deadline',  label: 'Sub. Deadline',    right: false, default: true },
  { key: 'sourcing_event_id',    label: 'Sourcing Event ID', right: false, default: false },
  { key: 'total_excl_vat',       label: 'Total excl VAT',   right: true,  default: true },
  { key: 'invoiced_sum',         label: 'Invoiced',         right: true,  default: true },
  { key: 'remaining_to_invoice', label: 'Remaining',        right: true,  default: true },
  { key: 'po_number',            label: 'PO',               right: false, default: false },
  { key: 'forecast_completion',  label: 'Exp. Completion',  right: false, default: true },
  { key: 'expiry',               label: 'Expires',          right: false, default: false },
  // Site surveys (migration 0408): shown to those who hold the Surveys page.
  { key: 'survey',               label: 'Survey',           right: false, default: true },
];
// SOW values are the sow:see-financials grant, and the API leaves them out for
// anybody else (pen test AD26341, AD12/AD16), so their columns are not offered.
const MONEY_COLUMNS = new Set(['total_excl_vat', 'invoiced_sum', 'remaining_to_invoice']);
const columnsFor = (surveys, money) => ALL_COLUMNS.filter(c => (c.key !== 'survey' || surveys) && (money || !MONEY_COLUMNS.has(c.key)));

// v2: added the Client PM column — bump so existing saved prefs pick up the new
// default set (and thus show the new column) rather than hiding it.
const PREFS_KEY = 'tracker_visible_cols_v2';
const SURVEY_COL_SEEN_KEY = 'tracker_survey_col_seen';

// Session-scoped view state (tab/search/sort/page/scroll) so that clicking into
// a SOW and pressing Back returns you to the exact tab and scroll position you
// left. sessionStorage (not localStorage) so it resets on a new tab / hard refresh.
const VIEW_KEY = 'tracker_view_state';

function loadViewState() {
  try {
    const s = sessionStorage.getItem(VIEW_KEY);
    if (s) return JSON.parse(s);
  } catch {}
  return {};
}

function loadColPrefs() {
  try {
    const stored = localStorage.getItem(PREFS_KEY);
    if (stored) {
      const cols = new Set(JSON.parse(stored));
      // The Survey column arrived after these preferences were saved: show it
      // once, and after that it stays however the person leaves it.
      if (!localStorage.getItem(SURVEY_COL_SEEN_KEY)) {
        cols.add('survey');
        localStorage.setItem(SURVEY_COL_SEEN_KEY, '1');
        localStorage.setItem(PREFS_KEY, JSON.stringify([...cols]));
      }
      return cols;
    }
  } catch {}
  return new Set(ALL_COLUMNS.filter(c => c.default).map(c => c.key));
}

function saveColPrefs(set) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify([...set])); } catch {}
}

// Defined outside TrackerPage so React never treats it as a new component type on re-render.
// Uses local pending state — only commits to colFilter on Enter (or clears on Escape).
function SortHeader({ col, label, right, sortCol, sortDir, toggleSort, colFilter, setColFilter }) {
  const active = sortCol === col;
  const [val, setVal] = useState(colFilter[col] || '');

  const commit = (v) => setColFilter(p => ({ ...p, [col]: v }));

  return (
    <th
      className={`px-2 py-1 text-[8px] uppercase font-bold border-b bg-gray-50 cursor-pointer select-none hover:bg-gray-100 ${right ? 'text-right' : 'text-left'}`}
      onClick={() => toggleSort(col)}
    >
      <span className={active ? 'text-gold' : 'text-gray-500'}>
        {label} {active ? (sortDir === 'asc' ? '↑' : '↓') : ''}
      </span>
      <div className="mt-0.5">
        <input
          type="text"
          value={val}
          onChange={e => { e.stopPropagation(); setVal(e.target.value); }}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => {
            e.stopPropagation();
            if (e.key === 'Enter') commit(val);
            if (e.key === 'Escape') { setVal(''); commit(''); }
          }}
          placeholder="filter… ↵"
          className="w-full text-[7px] border border-gray-200 rounded px-1 py-0 font-normal normal-case tracking-normal text-gray-600 bg-white focus:outline-none focus:border-gold"
        />
      </div>
    </th>
  );
}

// Status tabs that a deep link (e.g. #tracker/Expired from a notification email)
// may pre-select. Must match the status labels used for the tabs below.
const DEEP_LINK_TABS = new Set([
  'Active', 'Awaiting Payment', 'Issued', 'Complete', 'Cancelled', 'Expired', 'Not Won', 'Draft', 'On Hold', 'All',
]);

export default function TrackerPage() {
  const { navigate, detailId } = useApp();
  // Restore the last view (tab/sort/page/scroll) once per mount — see VIEW_KEY.
  const [savedView] = useState(loadViewState);
  const [sows, setSows] = useState([]);
  // A deep link like #tracker/Expired wins over the restored view so the email
  // button lands the reader on the exact tab it names. The hash arrives encoded,
  // so #tracker/Awaiting Payment is "Awaiting%20Payment" until decoded.
  const linkTab = (() => { try { return decodeURIComponent(detailId || ''); } catch { return detailId; } })();
  const [filter, setFilter] = useState(
    (linkTab && DEEP_LINK_TABS.has(linkTab)) ? linkTab : (savedView.filter || 'Active')
  );
  const [search, setSearch] = useState(savedView.search || '');
  const [page, setPage] = useState(savedView.page || 1);
  const [showCreate, setShowCreate] = useState(false);
  const [showArchived, setShowArchived] = useState(savedView.showArchived || false);
  const [clients, setClients] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCompletion, setEditingCompletion] = useState(null);
  const [sortCol, setSortCol] = useState(savedView.sortCol || 'updated_at');
  const [sortDir, setSortDir] = useState(savedView.sortDir || 'desc');
  const [colFilter, setColFilter] = useState(savedView.colFilter || {});
  const [visibleCols, setVisibleCols] = useState(loadColPrefs);
  const canSurveys = canAccess('surveys');
  const seesMoney = canAccess('sow:see-financials');
  const columns = columnsFor(canSurveys, seesMoney);
  const [showColPicker, setShowColPicker] = useState(false);
  const [showParentsOnly, setShowParentsOnly] = useState(savedView.showParentsOnly || false);
  const [createPrefill, setCreatePrefill] = useState(null);
  const didMountRef = useRef(false);
  const scrollRestoredRef = useRef(false);
  const pendingServiceExpiryLineIdRef = useRef(null);

  // Pick up a service-renewal prefill left by ServiceExpiryPage, if any.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SERVICE_RENEWAL_PREFILL_KEY);
      if (raw) {
        sessionStorage.removeItem(SERVICE_RENEWAL_PREFILL_KEY);
        const { service_renewal_id, service_expiry_line_id, ...formData } = JSON.parse(raw);
        pendingServiceExpiryLineIdRef.current = service_renewal_id || service_expiry_line_id || null;
        setCreatePrefill(formData);
        setShowCreate(true);
      }
    } catch { /* ignore malformed/blocked storage */ }
  }, []);

  const loadData = () => {
    Promise.all([
      api.get('sows'),
      api.get('clients'),
      api.get('people'),
      canSurveys ? api.get('surveys/by-opportunity').catch(() => ({})) : Promise.resolve(null),
    ]).then(([s, c, p, surveys]) => {
      // The survey summary rides on the row, so the column sorts and filters like the rest.
      setSows(surveys ? s.map(r => {
        const sum = surveys[r.id] ? surveySummary(surveys[r.id].required, surveys[r.id].counts) : null;
        return { ...r, survey: sum?.text || '', survey_tone: sum?.tone || '' };
      }) : s);
      setClients(c);
      setPeople(p);
      setLoading(false);
    });
  };

  useEffect(loadData, []);
  useRefreshOnFocus(loadData);
  // Reset to page 1 when the filters/sort change — but NOT on the initial mount,
  // otherwise it would clobber the page number restored from the saved view.
  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return; }
    setPage(1);
  }, [filter, search, sortCol, sortDir, colFilter, showParentsOnly]);

  // Persist the current view (tab/search/sort/page/filters) for Back navigation.
  // scrollTop is captured separately at the moment we open a SOW (see openSow).
  useEffect(() => {
    try {
      const prev = loadViewState();
      sessionStorage.setItem(VIEW_KEY, JSON.stringify({
        ...prev, filter, search, page, sortCol, sortDir, colFilter, showParentsOnly, showArchived,
      }));
    } catch {}
  }, [filter, search, page, sortCol, sortDir, colFilter, showParentsOnly, showArchived]);

  // Restore scroll position once the rows have loaded (only on the first load).
  useEffect(() => {
    if (loading || scrollRestoredRef.current) return;
    scrollRestoredRef.current = true;
    const top = savedView.scrollTop;
    if (top) {
      requestAnimationFrame(() => {
        const content = document.querySelector('.content');
        if (content) content.scrollTop = top;
      });
    }
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open a SOW, saving the current scroll position so Back restores it exactly.
  const openSow = (id) => {
    try {
      const content = document.querySelector('.content');
      const prev = loadViewState();
      sessionStorage.setItem(VIEW_KEY, JSON.stringify({
        ...prev, filter, search, page, sortCol, sortDir, colFilter, showParentsOnly, showArchived,
        scrollTop: content ? content.scrollTop : 0,
      }));
    } catch {}
    navigate('sow-detail', id);
  };

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => SORT_DIRS[d]);
    else { setSortCol(col); setSortDir('asc'); }
  };

  const visibleSows = showArchived
    ? sows.filter(s => s.status === 'Archived')
    : sows.filter(s => s.status !== 'Archived');

  // Parent-SOWs-only filter: show only SOWs that have at least one child
  const parentIds = new Set(sows.map(s => s.parent_sow_id).filter(Boolean));
  const parentCount = visibleSows.filter(s => parentIds.has(s.id)).length;

  // Base used for status counts and dashboard cards — respects the parents-only toggle
  const countBase = showParentsOnly ? visibleSows.filter(s => parentIds.has(s.id)) : visibleSows;

  const statusFiltered = showArchived
    ? countBase
    : (filter === 'All' ? countBase : countBase.filter(s => s.status === filter));

  const q = search.trim().toLowerCase();
  let filtered = q
    ? statusFiltered.filter(s =>
        (s.reference     || '').toLowerCase().includes(q) ||
        (s.owner_name    || '').toLowerCase().includes(q) ||
        (s.detail        || '').toLowerCase().includes(q) ||
        (s.po_number     || '').toLowerCase().includes(q) ||
        (s.sourcing_event_id || '').toLowerCase().includes(q) ||
        (s.client_name   || '').toLowerCase().includes(q)
      )
    : statusFiltered;

  // Column filters
  const cellVal = (s, col) => col === 'expiry' ? effExpiry(s) : s[col];
  for (const [col, val] of Object.entries(colFilter)) {
    if (!val?.trim()) continue;
    const v = val.trim().toLowerCase();
    filtered = filtered.filter(s => (String(cellVal(s, col) ?? '')).toLowerCase().includes(v));
  }

  // Sort
  filtered = [...filtered].sort((a, b) => {
    const av = cellVal(a, sortCol) ?? '';
    const bv = cellVal(b, sortCol) ?? '';
    const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageRows   = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // On the Issued tab, always surface the expiry column even if the user hasn't
  // enabled it, so they can see when each issued SOW is due to expire.
  const effectiveCols = filter === 'Issued' ? new Set([...visibleCols, 'expiry']) : visibleCols;

  const archivedCount = sows.filter(s => s.status === 'Archived').length;
  const statusCounts = {
    Active:    countBase.filter(s => s.status === 'Active').length,
    'Awaiting Payment': countBase.filter(s => s.status === 'Awaiting Payment').length,
    Issued:    countBase.filter(s => s.status === 'Issued').length,
    Complete:  countBase.filter(s => s.status === 'Complete').length,
    Cancelled: countBase.filter(s => s.status === 'Cancelled').length,
    Expired:   countBase.filter(s => s.status === 'Expired').length,
    'Not Won': countBase.filter(s => s.status === 'Not Won').length,
    Draft:     countBase.filter(s => s.status === 'Draft').length,
    'On Hold': countBase.filter(s => s.status === 'On Hold').length,
    All:       countBase.length,
  };

  const sumVal   = (arr) => arr.reduce((s, x) => s + (x.total_excl_vat || 0), 0);
  const sumRem   = (arr) => arr.reduce((s, x) => s + (x.remaining_to_invoice || 0), 0);
  const sumOut   = (arr) => arr.reduce((s, x) => s + (x.outstanding_sum || 0), 0);
  const bySt     = (st)  => countBase.filter(s => s.status === st);

  // ── "Remaining" ⇄ milestones reconciliation (Active book) ──────────────────
  // The Active card's "Remaining" is contract-anchored: total_excl_vat − invoiced
  // (Invoiced/Paid), floored at £0. The Milestones page is bottom-up from milestone
  // rows. They tie only when every Active SOW's milestones cover its contract total.
  // Decompose Remaining so any shortfall is visible where the number is read:
  //   Remaining = Awaiting + Ready to bill + Unreconciled
  // where "Unreconciled" is contract value with no live milestone behind it
  // (missing milestones, or cancelled value left in the total). That residual is
  // the admin worklist on the SOW Validation page.
  const activeSows    = sows.filter(s => s.status === 'Active');
  const recRemaining  = activeSows.reduce((s, x) => s + (x.remaining_to_invoice || 0), 0);
  const recAwaiting   = activeSows.reduce((s, x) => s + (x.awaiting_sum || 0), 0);
  const recReady      = activeSows.reduce((s, x) => s + (x.ready_to_bill_sum || 0), 0);
  const recUnrec      = recRemaining - recAwaiting - recReady;
  // SOWs whose Remaining doesn't equal Awaiting + Ready to bill (>£1) — need fixing.
  const recOffCount   = activeSows.filter(s =>
    Math.abs((s.remaining_to_invoice || 0) - (s.awaiting_sum || 0) - (s.ready_to_bill_sum || 0)) > 1
  ).length;
  const recShow       = seesMoney && (recRemaining > 0 || recOffCount > 0);
  const dashStats = [
    { label: 'Active',    color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', st: 'Active',   sows: bySt('Active') },
    { label: 'Awaiting Payment', color: 'text-teal-700', bg: 'bg-teal-50 border-teal-200', st: 'Awaiting Payment', sows: bySt('Awaiting Payment') },
    { label: 'Issued',    color: 'text-blue-700',    bg: 'bg-blue-50 border-blue-200',       st: 'Issued',   sows: bySt('Issued') },
    { label: 'Complete',  color: 'text-gray-700',    bg: 'bg-gray-50 border-gray-200',       st: 'Complete', sows: bySt('Complete') },
    { label: 'Draft',     color: 'text-amber-700',   bg: 'bg-amber-50 border-amber-200',     st: 'Draft',    sows: bySt('Draft') },
  ];

  const downloadCSV = () => {
    if (filtered.length === 0) return;
    const headers = ['Ref', 'Status', 'Project Manager', 'Delivery Owner', 'Client', 'Client Contact', 'Detail', 'Type', 'Commercial Type', 'Sourcing Event ID', ...(seesMoney ? ['Total excl VAT', 'Invoiced', 'Remaining'] : []), 'PO', 'Expected Completion'];
    const rows = filtered.map(s => [
      s.reference, s.status, s.owner_name || '', s.delivery_owner_name || '', s.client_name || '', s.client_contact_name || s.business_contact || '',
      s.detail || '', s.type || '', s.commercial_type || '', s.sourcing_event_id || '',
      ...(seesMoney ? [s.total_excl_vat || 0, s.invoiced_sum || 0, s.remaining_to_invoice || 0] : []),
      s.po_number || '', s.forecast_completion || '',
    ]);
    const csv = toCsv(headers, rows);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `opportunities-${filter.toLowerCase().replace(/\s+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCreate = async (data) => {
    const created = await api.post('sows', data);
    setShowCreate(false);
    setCreatePrefill(null);
    if (created?.id) {
      if (pendingServiceExpiryLineIdRef.current) {
        api.put(`service-renewals/${pendingServiceExpiryLineIdRef.current}`, { new_sow_id: created.id }).catch(() => {});
        pendingServiceExpiryLineIdRef.current = null;
      }
      navigate('sow-detail', created.id);
    } else {
      loadData();
    }
  };

  const saveCompletion = async (sowId, value) => {
    setEditingCompletion(null);
    await api.put(`sows/${sowId}`, { forecast_completion: value || null });
    loadData();
  };

  // shProps — passed explicitly so SortHeader (defined outside) stays the stable component type.
  // Do NOT wrap in a <SH> component here; an inline component wrapper recreates its reference on
  // every parent render, causing React to unmount/remount SortHeader (and lose input focus / fire
  // "n is not a function" errors during the in-flight click dispatch).
  const shProps = { sortCol, sortDir, toggleSort, colFilter, setColFilter };

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-base font-extrabold text-navy">Opportunity Tracker</h2>
          <div className="text-[9px] text-gray-500">{sows.length} opportunities total{q ? ` · ${filtered.length} matching` : ''}</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!showArchived && (
            <button
              onClick={() => { setShowParentsOnly(v => !v); setPage(1); }}
              className={`px-2 py-1.5 text-[9px] font-semibold rounded border whitespace-nowrap ${
                showParentsOnly
                  ? 'bg-navy text-white border-navy'
                  : 'bg-white border-gray-200 text-gray-400 hover:border-navy hover:text-navy'
              }`}
            >⊞ {showParentsOnly ? 'Parents only' : 'Parents only'} ({parentCount})</button>
          )}
          <button
            onClick={() => { setShowArchived(v => !v); setPage(1); }}
            className={`px-2 py-1.5 text-[9px] font-semibold rounded border whitespace-nowrap ${
              showArchived
                ? 'bg-amber-500 text-amber-950 border-amber-500'
                : 'bg-white border-gray-200 text-gray-400 hover:border-amber-400 hover:text-amber-600'
            }`}
          >📦 {showArchived ? 'Viewing Archived' : 'Show Archived'} ({archivedCount})</button>
          <div className="relative">
            <button onClick={() => setShowColPicker(v => !v)} title="Customise columns" className="px-2 py-1.5 bg-white border border-gray-200 text-navy text-[10px] font-semibold rounded hover:bg-gray-50 whitespace-nowrap">⚙ Columns</button>
            {showColPicker && (
              <div className="absolute right-0 top-8 z-30 bg-white border border-gray-200 rounded shadow-lg p-2 min-w-[170px]">
                <div className="text-[8px] font-bold text-gray-500 uppercase mb-1.5 px-1">Visible Columns</div>
                {columns.map(col => (
                  <label key={col.key} className="flex items-center gap-2 px-1 py-0.5 rounded cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" className="w-3 h-3 accent-gold" checked={visibleCols.has(col.key)}
                      onChange={e => {
                        const next = new Set(visibleCols);
                        if (e.target.checked) next.add(col.key); else next.delete(col.key);
                        setVisibleCols(next);
                        saveColPrefs(next);
                      }} />
                    <span className="text-[9px] text-gray-700">{col.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <button onClick={downloadCSV} className="px-3 py-1.5 bg-white border border-gray-200 text-navy text-[10px] font-semibold rounded hover:bg-gray-50 whitespace-nowrap">↓ Export CSV</button>
          {!showArchived && <button onClick={() => setShowCreate(true)} className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80 whitespace-nowrap">+ New Opportunity</button>}
        </div>
      </div>
      {!showArchived && !loading && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
          {dashStats.map(({ label, color, bg, st, sows: grp }) => (
            <button key={label} onClick={() => { setFilter(st); setPage(1); }}
              className={`text-left border rounded-lg p-3 transition-all hover:shadow-sm ${bg} ${filter === st ? 'ring-2 ring-offset-1 ring-navy/30' : ''}`}>
              <div className={`text-[8px] font-bold uppercase tracking-wide mb-1 ${color}`}>{label}</div>
              <div className={`text-xl font-extrabold ${color}`}>{grp.length}</div>
              {seesMoney && <div className="text-[8px] text-gray-500 mt-0.5">{formatMoney(sumVal(grp))} total</div>}
              {seesMoney && sumRem(grp) > 0 && <div className="text-[7px] text-gray-400">{formatMoney(sumRem(grp))} remaining</div>}
              {seesMoney && st === 'Awaiting Payment' && sumOut(grp) > 0 && <div className="text-[7px] text-gray-400">{formatMoney(sumOut(grp))} awaiting payment</div>}
            </button>
          ))}
        </div>
      )}
      {!showArchived && !loading && recShow && (
        <div className="mb-3 border border-gray-200 rounded-lg bg-gray-50 px-3 py-2">
          <div className="flex items-center justify-between flex-wrap gap-x-3 gap-y-1">
            <div className="text-[8px] font-bold uppercase tracking-wide text-gray-500">
              Active book · remaining reconciliation
            </div>
            {recOffCount > 0 && (
              <button
                onClick={() => navigate('sow-reconciliation')}
                className="text-[8px] font-semibold text-amber-700 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5 hover:bg-amber-100 whitespace-nowrap"
                title="These SOWs' milestones don't cover their contract total. Open Remaining Reconciliation to see and fix them."
              >⚠ {recOffCount} SOW{recOffCount !== 1 ? 's' : ''} don't reconcile → Fix</button>
            )}
          </div>
          <div className="mt-1 flex items-center flex-wrap gap-x-1.5 gap-y-1 text-[10px]">
            <span className="font-extrabold text-amber-600">{formatMoney(recRemaining)}</span>
            <span className="text-gray-400">remaining</span>
            <span className="text-gray-300">=</span>
            <span className="font-semibold text-gray-700">{formatMoney(recAwaiting)}</span>
            <span className="text-gray-400">awaiting</span>
            <span className="text-gray-300">+</span>
            <span className="font-semibold text-gray-700">{formatMoney(recReady)}</span>
            <span className="text-gray-400">ready to bill</span>
            <span className="text-gray-300">+</span>
            <span className={`font-semibold ${Math.abs(recUnrec) > 1 ? 'text-red-600' : 'text-gray-700'}`}>{formatMoney(recUnrec)}</span>
            <span className="text-gray-400" title="Contract value on Active SOWs with no live milestone behind it — missing milestones, or cancelled value still in the SOW total. This is what stops the tracker and the Milestones page tying up.">
              {recUnrec < -1 ? 'over-milestoned' : 'not yet on a milestone'}
            </span>
          </div>
          <div className="mt-1 text-[8px] text-gray-400">
            "Awaiting" + "Ready to bill" is what the Milestones page can show. The rest is a data gap for the admin team to close on SOW Validation.
          </div>
        </div>
      )}
      <div className="mb-2 flex items-center gap-2">
        <input
          className="form-input w-full max-w-xs"
          placeholder="Search ref, PM, detail, PO, sourcing ID, client…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {Object.values(colFilter).some(v => v?.trim()) && (
          <button
            onClick={() => setColFilter({})}
            className="px-2 py-1 text-[9px] font-semibold bg-amber-50 border border-amber-300 text-amber-700 rounded hover:bg-amber-100 whitespace-nowrap"
          >
            ✕ Reset Filters
          </button>
        )}
      </div>

      {!showArchived && (
        <StatusFilter
          value={filter}
          onChange={setFilter}
          options={[
            { key: 'Active',    label: 'Active',    count: statusCounts.Active },
            { key: 'Awaiting Payment', label: 'Awaiting Payment', count: statusCounts['Awaiting Payment'] },
            { key: 'Issued',    label: 'Issued',    count: statusCounts.Issued },
            { key: 'Complete',  label: 'Complete',  count: statusCounts.Complete },
            { key: 'Draft',     label: 'Draft',     count: statusCounts.Draft },
            { key: 'Cancelled', label: 'Cancelled', count: statusCounts.Cancelled },
            { key: 'Expired',   label: 'Expired',   count: statusCounts.Expired },
            { key: 'Not Won',   label: 'Not Won',   count: statusCounts['Not Won'] },
            { key: 'On Hold',   label: 'On Hold',   count: statusCounts['On Hold'] },
            { key: 'All',       label: 'All',       count: statusCounts.All },
          ]}
        />
      )}
      {showArchived && (
        <div className="mb-2 px-2 py-1 rounded bg-amber-50 border border-amber-200 text-[9px] text-amber-700 font-medium">
          Showing archived opportunities — hidden from all normal views. Use Restore on the SOW to return to Draft.
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-md overflow-x-auto" onClick={() => showColPicker && setShowColPicker(false)}>
        <table className="w-full">
          <thead>
            <tr>
              {columns.filter(c => effectiveCols.has(c.key)).map(col => (
                <SortHeader key={col.key} col={col.key} label={col.label} right={col.right} {...shProps} />
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((s) => (
              <tr key={s.id} className="hover:bg-gold/5 cursor-pointer" onClick={() => openSow(s.id)}>
                {visibleCols.has('reference') && <td className="px-2 py-1 text-[10px] font-bold text-gold whitespace-nowrap">{s.reference}</td>}
                {visibleCols.has('status') && <td className="px-2 py-1"><Tag status={s.status} /></td>}
                {visibleCols.has('owner_name') && <td className="px-2 py-1 text-[9px]">{s.owner_name || '-'}</td>}
                {visibleCols.has('delivery_owner_name') && <td className="px-2 py-1 text-[9px]">{s.delivery_owner_name || '-'}</td>}
                {visibleCols.has('client_name') && <td className="px-2 py-1 text-[9px] max-w-[100px] truncate">{s.client_name || '-'}</td>}
                {visibleCols.has('client_contact_name') && (
                  <td className="px-2 py-1 text-[9px] max-w-[100px] truncate" title={!s.client_contact_name && s.business_contact ? 'From Business Contact — no structured Client Contact set' : undefined}>
                    {s.client_contact_name || s.business_contact || '-'}
                  </td>
                )}
                {visibleCols.has('detail') && (
                  <td className="px-2 py-1 max-w-[200px]">
                    <div className="text-[10px] font-medium truncate">{s.detail}</div>
                    {s.closed_reason && <div className="text-[7px] text-red-500 font-semibold truncate">Closed: {s.closed_reason}</div>}
                    {parentIds.has(s.id) && (
                      <div className="text-[7px] text-navy font-semibold">
                        {sows.filter(c => c.parent_sow_id === s.id).length} child SOW{sows.filter(c => c.parent_sow_id === s.id).length !== 1 ? 's' : ''}
                      </div>
                    )}
                  </td>
                )}
                {visibleCols.has('type') && <td className="px-2 py-1 text-[9px]">{s.type || '-'}</td>}
                {visibleCols.has('commercial_type') && <td className="px-2 py-1 text-[9px] text-gray-500">{s.commercial_type || '-'}</td>}
                {visibleCols.has('submission_deadline') && (
                  <td className="px-2 py-1 text-[9px] whitespace-nowrap">
                    {s.submission_deadline
                      ? <span
                          className={`font-medium ${
                            SUBMITTED_STATUSES.has(s.status)
                              ? 'text-emerald-600'
                              : (new Date(s.submission_deadline) < new Date() ? 'text-red-500' : 'text-gray-700')
                          }`}
                          title={SUBMITTED_STATUSES.has(s.status) ? 'Submitted — deadline met' : undefined}
                        >{formatDate(s.submission_deadline)}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                )}
                {visibleCols.has('sourcing_event_id') && <td className="px-2 py-1 text-[9px] font-mono whitespace-nowrap">{s.sourcing_event_id || <span className="text-gray-300">—</span>}</td>}
                {seesMoney && visibleCols.has('total_excl_vat') && (() => {
                  // Flag when the SOW's live milestones don't reconcile to its
                  // contract total (>£1 gap) — this is what makes the tracker's
                  // "remaining" diverge from the Milestones "Awaiting" total.
                  const msSum = s.milestone_sum_excl;
                  const gap = (s.milestone_count > 0 && (s.total_excl_vat || 0) > 0 && msSum != null)
                    ? (msSum - (s.total_excl_vat || 0)) : 0;
                  const flagged = Math.abs(gap) >= 1;
                  return (
                    <td className="px-2 py-1 text-[10px] font-semibold text-right whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 justify-end">
                        {flagged && (
                          <span className="text-amber-500 cursor-help"
                            title={`Milestones total ${formatMoney(msSum)} but SOW total is ${formatMoney(s.total_excl_vat)} — ${formatMoney(Math.abs(gap))} ${gap > 0 ? 'over' : 'under'}. Milestones don't reconcile to the SOW total.`}>⚠</span>
                        )}
                        {formatMoney(s.total_excl_vat)}
                      </span>
                    </td>
                  );
                })()}
                {seesMoney && visibleCols.has('invoiced_sum') && <td className="px-2 py-1 text-[10px] text-right whitespace-nowrap">{formatMoney(s.invoiced_sum)}</td>}
                {seesMoney && visibleCols.has('remaining_to_invoice') && <td className="px-2 py-1 text-[10px] font-semibold text-amber-500 text-right whitespace-nowrap">{formatMoney(s.remaining_to_invoice)}</td>}
                {visibleCols.has('po_number') && <td className="px-2 py-1 text-[8px]">{s.po_number || '-'}</td>}
                {visibleCols.has('forecast_completion') && (
                  <td className="px-2 py-1 whitespace-nowrap" onClick={e => { e.stopPropagation(); setEditingCompletion(s.id); }}>
                    {editingCompletion === s.id ? (
                      <input
                        type="date"
                        className="form-input text-[9px] py-0 px-1 w-32"
                        defaultValue={s.forecast_completion || ''}
                        autoFocus
                        onBlur={e => saveCompletion(s.id, e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingCompletion(null); }}
                      />
                    ) : (
                      <span className={`text-[9px] cursor-text border-b border-dashed ${s.forecast_completion ? 'border-gray-300 text-gray-700' : 'border-gray-200 text-gray-400'}`} title="Click to edit">
                        {s.forecast_completion ? formatDate(s.forecast_completion) : 'Set date…'}
                      </span>
                    )}
                  </td>
                )}
                {effectiveCols.has('expiry') && (() => {
                  const exp = effExpiry(s);
                  const overdue = exp && new Date(`${exp}T00:00:00`) < new Date();
                  const soon = exp && !overdue && (new Date(`${exp}T00:00:00`) - new Date()) < 14 * 86400000;
                  return (
                    <td className="px-2 py-1 text-[9px] whitespace-nowrap">
                      {exp
                        ? <span className={`font-medium ${overdue ? 'text-red-600' : soon ? 'text-amber-600' : 'text-gray-700'}`}
                                title={s.expiry_date ? undefined : 'Default: 56 days after issue (no explicit expiry set)'}>
                            {formatDate(exp)}{!s.expiry_date && <span className="text-gray-400"> *</span>}
                          </span>
                        : <span className="text-gray-300">—</span>}
                    </td>
                  );
                })()}
                {canSurveys && visibleCols.has('survey') && (
                  <td className="px-2 py-1 whitespace-nowrap">
                    {s.survey ? <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${s.survey_tone}`}>{s.survey}</span> : <span className="text-gray-300">—</span>}
                  </td>
                )}
              </tr>
            ))}
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={effectiveCols.size} className="text-center py-6 text-[10px] text-gray-400">No opportunities found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-2 px-1">
          <div className="text-[9px] text-gray-400">
            Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}
              className="px-2 py-0.5 text-[9px] border border-gray-200 rounded bg-white disabled:opacity-40 hover:bg-gray-50">‹ Prev</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === '…'
                  ? <span key={`e${i}`} className="px-1 text-[9px] text-gray-400">…</span>
                  : <button key={p} onClick={() => setPage(p)}
                      className={`px-2 py-0.5 text-[9px] border rounded ${p === safePage ? 'bg-navy text-white border-gold' : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-600'}`}
                    >{p}</button>
              )}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}
              className="px-2 py-0.5 text-[9px] border border-gray-200 rounded bg-white disabled:opacity-40 hover:bg-gray-50">Next ›</button>
          </div>
        </div>
      )}

      {showCreate && (
        <CreateOpportunityModal
          clients={clients}
          people={people}
          initialData={createPrefill}
          onClose={() => { setShowCreate(false); setCreatePrefill(null); pendingServiceExpiryLineIdRef.current = null; }}
          onSave={handleCreate}
        />
      )}
    </div>
  );
}

// ── INLINE NEW CLIENT ─────────────────────────────────────────────────────────
function NewClientInline({ onCreated }) {
  const { toast } = useToast();
  const [show, setShow] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const r = await api.post('clients', { name: name.trim() });
      onCreated({ id: r.id, name: name.trim() });
      setName(''); setShow(false);
    } catch (e) { toast.error('Failed to create client: ' + e.message); }
    setSaving(false);
  };

  if (!show) return <button type="button" onClick={() => setShow(true)} className="text-[8px] text-gold underline ml-1 whitespace-nowrap">+ New</button>;
  return (
    <div className="mt-1 flex gap-1 items-center">
      <input className="form-input flex-1 text-[9px]" placeholder="Client name…" value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') create(); if (e.key === 'Escape') setShow(false); }}
        autoFocus />
      <button type="button" onClick={create} disabled={!name.trim() || saving} className="px-2 py-0.5 text-[8px] font-semibold bg-navy text-white rounded disabled:opacity-50">{saving ? '…' : 'Save'}</button>
      <button type="button" onClick={() => { setShow(false); setName(''); }} className="px-1.5 py-0.5 text-[8px] border border-gray-200 rounded text-gray-500">✕</button>
    </div>
  );
}

// ── INLINE NEW CLIENT CONTACT ─────────────────────────────────────────────────
function NewContactInline({ clientId, onCreated, nonCommercial = false }) {
  const { toast } = useToast();
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const create = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) return;
    setSaving(true);
    try {
      const is_non_commercial = nonCommercial ? 1 : 0;
      const r = await api.post('client-contacts', { ...form, client_id: clientId, is_non_commercial });
      onCreated({ id: r.id, first_name: form.first_name, last_name: form.last_name, email: form.email, is_non_commercial });
      setForm({ first_name: '', last_name: '', email: '', phone: '' });
      setShow(false);
    } catch (e) { toast.error('Failed to create contact: ' + e.message); }
    setSaving(false);
  };

  if (!show) return <button type="button" onClick={() => setShow(true)} className="text-[8px] text-gold underline ml-1 whitespace-nowrap">+ New</button>;
  return (
    <div className="mt-1 bg-blue-50 border border-blue-200 rounded p-2 space-y-1">
      <div className="text-[8px] font-bold text-blue-700 uppercase tracking-wide mb-1">{nonCommercial ? 'New Non-commercial Contact' : 'New Commercial Contact'}</div>
      <div className="grid grid-cols-2 gap-1">
        <input className="form-input text-[9px]" placeholder="First name *" value={form.first_name} onChange={e => set('first_name', e.target.value)} autoFocus />
        <input className="form-input text-[9px]" placeholder="Last name *" value={form.last_name} onChange={e => set('last_name', e.target.value)} />
        <input className="form-input text-[9px]" placeholder="Email" value={form.email} onChange={e => set('email', e.target.value)} />
        <input className="form-input text-[9px]" placeholder="Phone" value={form.phone} onChange={e => set('phone', e.target.value)} />
      </div>
      <div className="flex gap-1">
        <button type="button" onClick={create} disabled={!form.first_name.trim() || !form.last_name.trim() || saving} className="px-2 py-0.5 text-[8px] font-semibold bg-navy text-white rounded disabled:opacity-50">{saving ? '…' : 'Save Contact'}</button>
        <button type="button" onClick={() => setShow(false)} className="px-1.5 py-0.5 text-[8px] border border-gray-200 rounded text-gray-500">Cancel</button>
      </div>
    </div>
  );
}

// ── CREATE OPPORTUNITY MODAL ──────────────────────────────────────────────────
const REQUIRED_FIELDS = ['reference','detail','client_id','client_contact_id','owner_id','type','commercial_type','source','received_date'];
// delivery_owner_id is intentionally not required — defaults to blank

function CreateOpportunityModal({ clients, people, onClose, onSave, initialData }) {
  const [form, setForm] = useState({
    reference: '', detail: '', status: 'Draft', type: '', commercial_type: '',
    client_id: '', client_contact_id: '', non_commercial_contact_id: '', owner_id: '', delivery_owner_id: '', source: 'Enquiry',
    received_date: new Date().toISOString().split('T')[0], submission_deadline: '', sourcing_event_id: '', total_excl_vat: '',
    po_number: '', payment_terms: 30,
    sage_quote: '', forecast_completion: '', notes: '', magnitude: '',
    parent_sow_id: '',
    ...(initialData || {}),
  });
  const [localClients, setLocalClients] = useState(clients);
  const [sowTypes, setSowTypes] = useState([]);
  const [commercialTypes, setCommercialTypes] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [errors, setErrors] = useState({});
  const [parentSearch, setParentSearch] = useState('');
  const [allSows, setAllSows] = useState([]);
  const [showParentDropdown, setShowParentDropdown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const parentRequired = typeRequiresParent(form.type);

  const handleClientCreated = (c) => {
    setLocalClients(prev => [...prev, c].sort((a, b) => a.name.localeCompare(b.name)));
    set('client_id', c.id);
    setContacts([]);
  };

  const handleContactCreated = (key) => (c) => {
    setContacts(prev => [...prev, c]);
    set(key, c.id);
  };

  useEffect(() => {
    api.get('sows/next-ref').then(r => set('reference', r.reference)).catch(() => {});
    api.get('config-options', { category: 'sow_type' })
      .then(opts => { setSowTypes(opts || []); })
      .catch(() => setSowTypes([{ label: 'PI/TI' }, { label: 'Pass-Through' }, { label: 'Support' }]));
    api.get('config-options', { category: 'commercial_type' })
      .then(opts => setCommercialTypes(opts || []))
      .catch(() => setCommercialTypes([{ label: 'Fixed Price' }, { label: 'T&M' }]));
    api.get('config-options', { category: 'sow_source' })
      .then(opts => setSourceOptions(Array.isArray(opts) && opts.length ? opts : [
        { value: 'Enquiry', label: 'Enquiry' }, { value: 'Internal', label: 'Internal' },
        { value: 'Historical', label: 'Historical' }, { value: 'Email Request', label: 'Email Request' },
        { value: 'Sourcing Event/SOR', label: 'Sourcing Event / SOR' }, { value: 'Verbal', label: 'Verbal' },
      ]))
      .catch(() => setSourceOptions([
        { value: 'Enquiry', label: 'Enquiry' }, { value: 'Internal', label: 'Internal' },
        { value: 'Historical', label: 'Historical' }, { value: 'Email Request', label: 'Email Request' },
        { value: 'Sourcing Event/SOR', label: 'Sourcing Event / SOR' }, { value: 'Verbal', label: 'Verbal' },
      ]));
  }, []);

  // Load contacts whenever client changes
  useEffect(() => {
    if (!form.client_id) { setContacts([]); return; }
    api.get('client-contacts', { client_id: form.client_id })
      .then(c => setContacts(c || []))
      .catch(() => setContacts([]));
  }, [form.client_id]);

  // Load SOW list for parent selection
  useEffect(() => {
    api.get('sows', { limit: 1000 })
      .then(r => setAllSows(Array.isArray(r) ? r : (r?.results || [])))
      .catch(() => {});
  }, []);

  // When parent SOW selected, inherit client
  useEffect(() => {
    if (!form.parent_sow_id) return;
    const p = allSows.find(s => s.id === form.parent_sow_id);
    if (p?.client_id) set('client_id', p.client_id);
  }, [form.parent_sow_id, allSows]);

  const parentSow = allSows.find(s => s.id === form.parent_sow_id) || null;
  const filteredParentSows = parentSearch.trim().length > 0
    ? allSows.filter(s => s.reference?.toLowerCase().includes(parentSearch.toLowerCase()) || s.detail?.toLowerCase().includes(parentSearch.toLowerCase())).slice(0, 8)
    : [];

  const validate = () => {
    const errs = {};
    for (const f of REQUIRED_FIELDS) {
      if (!form[f]?.toString().trim()) errs[f] = true;
    }
    if (parentRequired && !form.parent_sow_id) errs.parent_sow_id = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    setSaveError('');
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave(form);
    } catch (e) {
      setSaveError(e?.message || 'The opportunity could not be created.');
      setSaving(false);
    }
  };

  const field = (key) => errors[key] ? 'border-red-400 ring-1 ring-red-300' : '';
  const req = (label) => <>{label} <span className="text-red-500">*</span></>;

  return (
    <Modal title="New Opportunity" onClose={onClose} wide footer={
      <>
        {saveError && <span className="mr-auto text-[9px] font-semibold text-red-700">{saveError}</span>}
        <button onClick={onClose} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white hover:bg-gray-50">Cancel</button>
        <button onClick={handleSave} disabled={saving} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50">{saving ? 'Creating…' : 'Create Opportunity'}</button>
      </>
    }>
      {Object.keys(errors).length > 0 && (
        <div className="mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-[9px] text-red-700 font-semibold">
          Please complete all required fields marked with *
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2 relative">
          <label className={`block text-[8px] font-bold uppercase mb-0.5 ${parentRequired && errors.parent_sow_id ? 'text-red-500' : 'text-gray-500'}`}>
            {parentRequired ? req('Parent Opportunity') : 'Parent Opportunity'}{' '}
            <span className="font-normal normal-case text-gray-400">({parentRequired ? `required for ${form.type.trim()}` : 'optional'}: links this SOW to a parent's budget &amp; PO)</span>
          </label>
          {parentSow ? (
            <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-[10px]">
              <span className="font-mono font-bold text-navy">{parentSow.reference}</span>
              <span className="text-gray-500 truncate">{parentSow.detail}</span>
              <button type="button" onClick={() => { set('parent_sow_id', ''); setParentSearch(''); }} className="ml-auto text-gray-400 hover:text-red-500 text-xs leading-none">✕</button>
            </div>
          ) : (
            <>
              <input
                className={`form-input ${parentRequired ? field('parent_sow_id') : ''}`}
                placeholder="Search by reference or detail…"
                value={parentSearch}
                onChange={e => { setParentSearch(e.target.value); setShowParentDropdown(true); }}
                onFocus={() => setShowParentDropdown(true)}
                onBlur={() => setTimeout(() => setShowParentDropdown(false), 150)}
              />
              {showParentDropdown && filteredParentSows.length > 0 && (
                <div className="absolute z-50 left-0 right-0 top-full mt-0.5 bg-white border border-gray-200 rounded shadow-lg max-h-40 overflow-y-auto">
                  {filteredParentSows.map(s => (
                    <button key={s.id} type="button"
                      className="w-full text-left px-2 py-1.5 hover:bg-blue-50 text-[10px] flex items-center gap-2"
                      onMouseDown={() => { set('parent_sow_id', s.id); setParentSearch(''); setShowParentDropdown(false); }}>
                      <span className="font-mono font-bold text-navy whitespace-nowrap">{s.reference}</span>
                      <span className="text-gray-500 truncate">{s.detail}</span>
                      <span className="ml-auto text-gray-400 whitespace-nowrap">{s.client_name}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
          {parentRequired && errors.parent_sow_id && !form.parent_sow_id && (
            <p className="mt-0.5 text-[9px] text-red-600">{form.type.trim()} opportunities need a parent opportunity. Search for it by reference or detail.</p>
          )}
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">{req('Reference')}</label>
          <input className={`form-input ${field('reference')}`} value={form.reference} onChange={e => set('reference', e.target.value)} placeholder="AV-40XXXX" />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Status</label>
          <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
            <option>Draft</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">{req('Detail')}</label>
          <input className={`form-input ${field('detail')}`} value={form.detail} onChange={e => set('detail', e.target.value)} placeholder="Project description" />
        </div>
        <div className="col-span-2">
          <div className="flex items-center justify-between mb-0.5">
            <label className={`text-[8px] font-bold uppercase ${errors.client_id ? 'text-red-500' : 'text-gray-500'}`}>{req('Client')}</label>
            <NewClientInline onCreated={handleClientCreated} />
          </div>
          <select className={`form-select ${field('client_id')}`} value={form.client_id}
            onChange={e => setForm(p => ({ ...p, client_id: e.target.value, client_contact_id: '', non_commercial_contact_id: '' }))}>
            <option value="">Select...</option>
            {localClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <div className="flex items-center justify-between mb-0.5">
            <label className={`text-[8px] font-bold uppercase ${errors.client_contact_id ? 'text-red-500' : 'text-gray-500'}`}>{req('Commercial Contact')}</label>
            {form.client_id && <NewContactInline clientId={form.client_id} onCreated={handleContactCreated('client_contact_id')} />}
          </div>
          <select className={`form-select ${field('client_contact_id')}`} value={form.client_contact_id}
            onChange={e => set('client_contact_id', e.target.value)}
            disabled={!form.client_id}>
            <option value="">{form.client_id ? 'Select contact...' : 'Select client first'}</option>
            {contactOptions(contacts, { selectedId: form.client_contact_id, excludeId: form.non_commercial_contact_id })
              .map(c => <option key={c.id} value={c.id}>{contactOptionText(c)}</option>)}
          </select>
        </div>
        <div>
          <div className="flex items-center justify-between mb-0.5">
            <label className="text-[8px] font-bold text-gray-500 uppercase">Non-commercial Contact <span className="font-normal normal-case text-gray-400">(optional)</span></label>
            {form.client_id && <NewContactInline clientId={form.client_id} nonCommercial onCreated={handleContactCreated('non_commercial_contact_id')} />}
          </div>
          <NonCommercialContactSelect contacts={contacts} clientId={form.client_id}
            value={form.non_commercial_contact_id} commercialId={form.client_contact_id}
            onChange={v => set('non_commercial_contact_id', v)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">{req('Project Manager')}</label>
          <select className={`form-select ${field('owner_id')}`} value={form.owner_id} onChange={e => set('owner_id', e.target.value)}>
            <option value="">Select...</option>
            {people.filter(p => p.employee_status !== 'left' && p.employee_status !== 'archived').map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Delivery Owner</label>
          <select className="form-select" value={form.delivery_owner_id} onChange={e => set('delivery_owner_id', e.target.value)}>
            <option value="">— none —</option>
            {people.filter(p => p.employee_status !== 'left' && p.employee_status !== 'archived').map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">{req('Type')}</label>
          <select className={`form-select ${field('type')}`} value={form.type} onChange={e => set('type', e.target.value)}>
            <option value="">Select type...</option>
            {sowTypes.map(t => { const lbl = t.label || t.value || t; return <option key={lbl} value={lbl}>{lbl}</option>; })}
          </select>
          {parentRequired && !form.parent_sow_id && (
            <p className="mt-0.5 text-[9px] text-amber-700">{form.type.trim()} needs a parent opportunity: choose it under Parent Opportunity at the top of this form.</p>
          )}
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">{req('Commercial Type')}</label>
          <select className={`form-select ${field('commercial_type')}`} value={form.commercial_type} onChange={e => set('commercial_type', e.target.value)}>
            <option value="">Select...</option>
            {commercialTypes.map(t => { const lbl = t.label || t.value || t; return <option key={lbl} value={lbl}>{lbl}</option>; })}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">{req('Source')}</label>
          <select className={`form-select ${field('source')}`} value={form.source} onChange={e => set('source', e.target.value)}>
            <option value="">Select source…</option>
            {sourceOptions.map(o => <option key={o.value || o.label} value={o.value || o.label}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">{req('Date Received')}</label>
          <input className={`form-input ${field('received_date')}`} type="date" value={form.received_date} onChange={e => set('received_date', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Submission Deadline</label>
          <input className="form-input" type="date" value={form.submission_deadline} onChange={e => set('submission_deadline', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Sourcing Event ID</label>
          <input className="form-input" value={form.sourcing_event_id} onChange={e => set('sourcing_event_id', e.target.value)} placeholder="e.g. SOR-12345 / sourcing event ref" />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Expected Magnitude</label>
          <select className="form-select" value={form.magnitude} onChange={e => set('magnitude', e.target.value)}>
            <option value="">Select...</option>
            <option value="Small">Small</option>
            <option value="Medium">Medium</option>
            <option value="Large">Large</option>
          </select>
        </div>
        {canAccess('sow:see-financials') && (
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Total excl VAT</label>
          <input className="form-input" type="number" step="0.01" value={form.total_excl_vat} onChange={e => set('total_excl_vat', parseFloat(e.target.value) || '')} />
        </div>
        )}
        {!form.parent_sow_id && (
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">PO Number</label>
            <input className="form-input" value={form.po_number} onChange={e => set('po_number', e.target.value)} placeholder="e.g. 6000-234234" />
          </div>
        )}
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Payment Terms (days)</label>
          {form.parent_sow_id ? (
            <div className="form-input bg-gray-50 text-gray-500 text-[10px]">Inherited from parent ({parentSow?.payment_terms ?? 30} days)</div>
          ) : (
            <input className="form-input" type="number" value={form.payment_terms} onChange={e => set('payment_terms', parseInt(e.target.value) || 30)} />
          )}
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Expected Completion</label>
          <input className="form-input" type="date" value={form.forecast_completion} onChange={e => set('forecast_completion', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Sage Quote</label>
          <input className="form-input" value={form.sage_quote} onChange={e => set('sage_quote', e.target.value)} />
        </div>
      </div>
      <div className="mt-2">
        <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Notes <span className="font-normal text-gray-400 normal-case">(auto-posted as first comment)</span></label>
        <textarea className="form-textarea" rows="2" value={form.notes} onChange={e => set('notes', e.target.value)} />
      </div>
    </Modal>
  );
}
